import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material"
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material"
import {
  AccreditationStatus,
  DocumentClassification,
  IssueStatus,
  Person,
  Report,
  ReportRevision,
} from "graphql/types"
import React, { useState } from "react"

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-settings-tabpanel-${index}`}
      aria-labelledby={`report-settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  )
}

interface ReportSettingsDialogProps {
  open: boolean
  onClose: () => void
  report: Report
  onSave: (updatedReport: Partial<Report>) => void
  employees?: Person[]
}

export const ReportSettingsDialog: React.FC<ReportSettingsDialogProps> = ({
  open,
  onClose,
  report,
  onSave,
  employees = [],
}) => {
  const [tabValue, setTabValue] = useState(0)

  // Document Settings State
  const [issueStatus, setIssueStatus] = useState<IssueStatus>(
    report.issueStatus || IssueStatus.DRAFT
  )
  const [showDraftWatermark, setShowDraftWatermark] = useState(report.showDraftWatermark ?? true)
  const [accreditationStatus, setAccreditationStatus] = useState<AccreditationStatus>(
    report.accreditationStatus || AccreditationStatus.ACCREDITED
  )

  // Document Info State
  const [documentNo, setDocumentNo] = useState(report.documentNo || "")
  const [assignmentNo, setAssignmentNo] = useState(report.assignmentNo || "")
  const [releaseDate, setReleaseDate] = useState(report.releaseDate || "")
  const [formNumber, setFormNumber] = useState(report.formNumber || "FRM-708-02")
  const [formRevision, setFormRevision] = useState(
    report.formRevision || new Date().toISOString().split("T")[0]
  )
  const [testFacility, setTestFacility] = useState(report.testFacility || "TREO-Hamburg")

  // Approval State
  const [reviewedBy, setReviewedBy] = useState<Person | null>(report.reviewedBy || null)
  const [reviewerTitle, setReviewerTitle] = useState(report.reviewerTitle || "Laborleiter")
  // Removed report-level approval fields; approval belongs to revisions

  // Revision History State
  interface EditingRevision extends Omit<ReportRevision, "id"> {
    id: string
    isEditing?: boolean
    isNew?: boolean
  }

  const [revisions, setRevisions] = useState<EditingRevision[]>(
    report.revisions?.map((rev) => ({ ...rev, isEditing: false, isNew: false })) || []
  )
  const [editingRevision, setEditingRevision] = useState<EditingRevision | null>(null)

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue)
  }

  const handleSave = () => {
    const cleanRevisions: ReportRevision[] = revisions.map(({ isEditing, isNew, ...rev }) => rev)
    const updatedReport: Partial<Report> = {
      classification: DocumentClassification.NOT_CLASSIFIED,
      issueStatus,
      showDraftWatermark,
      accreditationStatus,
      documentNo,
      assignmentNo,
      releaseDate,
      formNumber,
      formRevision,
      testFacility,
      reviewedBy,
      reviewerTitle,
      revisions: cleanRevisions,
    }

    onSave(updatedReport)
    onClose()
  }

  // Classification is fixed to NOT_CLASSIFIED; no options shown in UI

  const issueStatusOptions = [
    { value: IssueStatus.DRAFT, label: "Entwurf" },
    { value: IssueStatus.REVIEW, label: "Prüfung" },
    { value: IssueStatus.APPROVED, label: "Genehmigt" },
    { value: IssueStatus.FINAL, label: "Endgültig" },
    { value: IssueStatus.ARCHIVED, label: "Archiviert" },
  ]

  const accreditationOptions = [
    { value: AccreditationStatus.ACCREDITED, label: "Akkreditiert" },
    { value: AccreditationStatus.NON_ACCREDITED, label: "Nicht akkreditiert (*)" },
    { value: AccreditationStatus.EXTERNAL_ACCREDITED, label: "Extern akkreditiert (**)" },
    {
      value: AccreditationStatus.EXTERNAL_NON_ACCREDITED,
      label: "Extern nicht akkreditiert (***)",
    },
  ]

  const testFacilityOptions = [
    "TREO-Hamburg",
    "TREO-Neumünster",
    "Externes Labor",
    "Kundenstandort",
  ]

  // Revision handlers
  const getNextRevisionNumber = () => {
    if (revisions.length === 0) return "1.0"
    const lastRevision = revisions[revisions.length - 1]
    const parts = lastRevision.revisionNumber.split(".")
    if (parts.length === 2) {
      const major = parseInt(parts[0])
      const minor = parseInt(parts[1])
      return `${major}.${minor + 1}`
    }
    return "1.0"
  }

  const handleAddRevision = () => {
    const newRevision: EditingRevision = {
      id: crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`,
      revisionNumber: getNextRevisionNumber(),
      date: new Date().toISOString().split("T")[0],
      affectedSections: "",
      issuedBy: null,
      reasonsForRevision: "",
      isEditing: true,
      isNew: true,
    }
    setEditingRevision(newRevision)
    setRevisions([...revisions, newRevision])
  }

  const handleEditRevision = (revision: EditingRevision) => {
    setEditingRevision({ ...revision, isEditing: true })
    setRevisions(
      revisions.map((r) => (r.id === revision.id ? { ...revision, isEditing: true } : r))
    )
  }

  const handleSaveRevision = () => {
    if (!editingRevision) return
    setRevisions(
      revisions.map((r) =>
        r.id === editingRevision.id ? { ...editingRevision, isEditing: false, isNew: false } : r
      )
    )
    setEditingRevision(null)
  }

  const handleCancelEdit = () => {
    if (!editingRevision) return
    if (editingRevision.isNew) {
      setRevisions(revisions.filter((r) => r.id !== editingRevision.id))
    } else {
      setRevisions(
        revisions.map((r) => (r.id === editingRevision.id ? { ...r, isEditing: false } : r))
      )
    }
    setEditingRevision(null)
  }

  const handleDeleteRevision = (id: string) => {
    setRevisions(revisions.filter((r) => r.id !== id))
  }

  const handleRevisionFieldChange = (field: keyof EditingRevision, value: any) => {
    if (!editingRevision) return
    setEditingRevision({ ...editingRevision, [field]: value })
  }

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>Berichtseinstellungen</DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Dokumenteinstellungen" />
            <Tab label="Dokumentinformationen" />
            <Tab label="Freigabe" />
            <Tab label="Revisionen" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Grid2 container spacing={3}>
            {/* Klassifizierung ist fest: Nicht klassifiziert */}

            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Ausgabestatus</InputLabel>
                <Select
                  value={issueStatus}
                  onChange={(e) => setIssueStatus(e.target.value as IssueStatus)}
                  label="Ausgabestatus"
                >
                  {issueStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Akkreditierungsstatus</InputLabel>
                <Select
                  value={accreditationStatus}
                  onChange={(e) => setAccreditationStatus(e.target.value as AccreditationStatus)}
                  label="Akkreditierungsstatus"
                >
                  {accreditationOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showDraftWatermark}
                    onChange={(e) => setShowDraftWatermark(e.target.checked)}
                    disabled={issueStatus !== IssueStatus.DRAFT}
                  />
                }
                label="ENTWURF Draft anzeigen"
              />
              {issueStatus !== IssueStatus.DRAFT && (
                <Typography variant="caption" color="textSecondary" display="block">
                  Wasserzeichen nur für Entwurfsdokumente verfügbar
                </Typography>
              )}
            </Grid2>
          </Grid2>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Dokumentennummer"
                value={documentNo}
                onChange={(e) => setDocumentNo(e.target.value)}
                placeholder="z.B. TR-2024-001"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Auftragsnummer"
                value={assignmentNo}
                onChange={(e) => setAssignmentNo(e.target.value)}
                placeholder="z.B. 24-123456"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Freigabedatum"
                type="date"
                value={releaseDate}
                onChange={(e) => setReleaseDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Autocomplete
                value={testFacility}
                onChange={(_, newValue) => setTestFacility(newValue || "")}
                options={testFacilityOptions}
                freeSolo
                renderInput={(params) => <TextField {...params} label="Prüfeinrichtung" />}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Formularnummer"
                value={formNumber}
                onChange={(e) => setFormNumber(e.target.value)}
                placeholder="z.B. FRM-708-02"
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Formularrevision"
                type="date"
                value={formRevision}
                onChange={(e) => setFormRevision(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid2>
          </Grid2>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Grid2 container spacing={3}>
            <Grid2 size={{ xs: 12 }}>
              <Typography variant="subtitle2" gutterBottom>
                Prüfung
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <Autocomplete
                value={
                  reviewedBy ? employees.find((e) => e.id === reviewedBy.id) || reviewedBy : null
                }
                onChange={(_, newValue) => setReviewedBy(newValue)}
                options={employees}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                getOptionLabel={(option) =>
                  `${option.firstName || ""} ${option.lastName || ""}`.trim() || option.email || ""
                }
                renderInput={(params) => <TextField {...params} label="Geprüft von" />}
              />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Titel des Prüfers"
                value={reviewerTitle}
                onChange={(e) => setReviewerTitle(e.target.value)}
                placeholder="z.B. Laborleiter"
              />
            </Grid2>
          </Grid2>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2" gutterBottom>
              Revisionen
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="contained"
              size="small"
              onClick={handleAddRevision}
              disabled={editingRevision !== null}
            >
              Revision hinzufügen
            </Button>
          </Box>
          <Divider sx={{ mb: 2, mt: 1 }} />

          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Revision</TableCell>
                  <TableCell>Datum</TableCell>
                  <TableCell>Betroffene Abschnitte</TableCell>
                  <TableCell>Ausgestellt von</TableCell>
                  <TableCell>Gründe</TableCell>
                  <TableCell align="center">Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revisions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="textSecondary">
                        Noch keine Revisionen. Klicken Sie auf "Revision hinzufügen".
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  revisions.map((revision) => (
                    <TableRow key={revision.id}>
                      {revision.isEditing && editingRevision?.id === revision.id ? (
                        <>
                          <TableCell>
                            <TextField
                              size="small"
                              value={editingRevision.revisionNumber}
                              onChange={(e) =>
                                handleRevisionFieldChange("revisionNumber", e.target.value)
                              }
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="date"
                              value={editingRevision.date}
                              onChange={(e) => handleRevisionFieldChange("date", e.target.value)}
                              fullWidth
                              InputLabelProps={{ shrink: true }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={editingRevision.affectedSections}
                              onChange={(e) =>
                                handleRevisionFieldChange("affectedSections", e.target.value)
                              }
                              fullWidth
                              multiline
                              rows={2}
                            />
                          </TableCell>
                          <TableCell>
                            <Autocomplete
                              size="small"
                              value={editingRevision.issuedBy}
                              onChange={(_, newValue) =>
                                handleRevisionFieldChange("issuedBy", newValue)
                              }
                              options={employees}
                              isOptionEqualToValue={(option, value) => option?.id === value?.id}
                              getOptionLabel={(option) =>
                                `${option.firstName || ""} ${option.lastName || ""}`.trim() ||
                                option.email ||
                                ""
                              }
                              renderInput={(params) => <TextField {...params} />}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={editingRevision.reasonsForRevision}
                              onChange={(e) =>
                                handleRevisionFieldChange("reasonsForRevision", e.target.value)
                              }
                              fullWidth
                              multiline
                              rows={2}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton size="small" color="primary" onClick={handleSaveRevision}>
                              <SaveIcon />
                            </IconButton>
                            <IconButton size="small" color="default" onClick={handleCancelEdit}>
                              <CancelIcon />
                            </IconButton>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{revision.revisionNumber}</TableCell>
                          <TableCell>{revision.date}</TableCell>
                          <TableCell>{revision.affectedSections || "-"}</TableCell>
                          <TableCell>
                            {(() => {
                              if (!revision.issuedBy) return "-"
                              const hasName = !!(
                                revision.issuedBy.firstName || revision.issuedBy.lastName
                              )
                              if (hasName) {
                                return (
                                  `${revision.issuedBy.firstName || ""} ${revision.issuedBy.lastName || ""}`.trim() ||
                                  "-"
                                )
                              }
                              const match = employees.find((e) => e.id === revision.issuedBy?.id)
                              if (match) {
                                const name =
                                  `${match.firstName || ""} ${match.lastName || ""}`.trim()
                                return name || match.email || "-"
                              }
                              return "-"
                            })()}
                          </TableCell>
                          <TableCell>{revision.reasonsForRevision}</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleEditRevision(revision)}
                              disabled={editingRevision !== null}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteRevision(revision.id)}
                              disabled={editingRevision !== null}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {revisions.length === 0 && (
            <Box sx={{ mt: 3, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
              <Typography variant="body2" color="info.dark">
                Hinweis: Wenn keine Revisionen angegeben sind, wird bei der Report-Erstellung
                automatisch ein Ersteintrag erzeugt.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Einstellungen speichern
        </Button>
      </DialogActions>
    </Dialog>
  )
}
